import { headerSearchSX } from '@/components/content/Header/styles/search';
import { useProductsWithInfoElectronico } from '@/data/Content/_Products_InfoElectronico';
import { useSearchNavigation } from '@/data/Content/SearchNavigation';
import { useLocalization } from '@/data/Localization';
import SearchIcon from '@mui/icons-material/Search';
import { Button, IconButton, InputAdornment, TextField } from '@mui/material';
import { useEffect, useState, forwardRef, useImperativeHandle, useCallback, useRef } from 'react';

export interface HeaderSearchHandle {
	clearSearch: () => void;
	isLoading?: boolean;
}

interface HeaderSearchProps {
	mobile?: boolean;
	onFocus?: () => void;
	onBlur?: () => void;
	onSuggestChange?: (suggest: any) => void;
	onSuggestProductsChange?: (suggest: any) => void;
	onSearchValue?: (value: any) => void;
	isSearchOpen?: boolean;
}

export const HeaderSearch = forwardRef<HeaderSearchHandle, HeaderSearchProps>(
	(
		{
			mobile,
			onFocus,
			onSuggestChange,
			onSuggestProductsChange,
			onSearchValue,
			isSearchOpen,
			onBlur,
		},
		ref
	) => {
		const { searchValue, suggest, suggestProducts, onInputChange, onSubmit, onChange, isLoading } =
			useSearchNavigation();
		const SearchNLS = useLocalization('SearchBar');
		const inputRef = useRef<HTMLInputElement>(null);
		const uniqueId = `type-ahead-site-search-${mobile ? 'mobile' : 'desktop'}`;
		const [searchValueText, setSearchValueText] = useState('');

		const handleClearSearch = useCallback(() => {
			onChange('');
			onInputChange('');
			setSearchValueText('');
		}, [onChange, onInputChange]);

		useEffect(() => {
			if (onSuggestChange) {
				onSuggestChange(suggest);
			}
		}, [suggest, onSuggestChange]);

		const { data: productsWithInfoElectronico } = useProductsWithInfoElectronico(
			suggestProducts ?? []
		);

		useEffect(() => {
			if (onSuggestProductsChange) {
				onSuggestProductsChange(productsWithInfoElectronico);
			}
		}, [productsWithInfoElectronico, onSuggestProductsChange]);

		useEffect(() => {
			if (onSearchValue) {
				onSearchValue(searchValue);
			}
		}, [searchValue, onSearchValue]);

		const handleSearchClick = () => {
			if (onFocus) onFocus();
		};

		useImperativeHandle(ref, () => ({
			clearSearch: handleClearSearch,
			isLoading,
		}));

		const handleInputChange = (
			event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
		) => {
			const regex = /^[a-zA-Z0-9ñÑáéíóúüÁÉÍÓÚÜ.\s-]*$/;
			const { value } = event.target;
			if (regex.test(value)) {
				onInputChange(value);
				setSearchValueText(value);
				return;
			}
		};

		const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Enter' && searchValueText) {
				onBlur?.();
				onSubmit({ label: searchValueText });
			}
		};

		return (
			<TextField
				value={searchValueText}
				sx={headerSearchSX({ isMobile: mobile, expanded: !!isSearchOpen })}
				inputProps={{ 'data-testid': uniqueId, ref: inputRef }}
				InputProps={{
					placeholder: SearchNLS.SearchField.t(),
					slotProps: {
						input: { role: undefined, 'aria-expanded': undefined },
					},
					size: 'small',
					sx: { fontSize: { xs: 'var(--font-size-body)', sm: '16px' }, height: '38px' },
					onKeyDown: handleKeyDown, // Manejador de evento "Enter"
					onFocus: !mobile ? handleSearchClick : void 0,
					startAdornment: (
						<InputAdornment position="start" sx={{ marginRight: '0px' }}>
							<IconButton
								sx={{
									color: '#2C2C2C',
									'&:disabled': {
										color: '#2C2C2C',
									},
								}}
								aria-label={SearchNLS.SearchField.t()}
								disabled={!!(searchValueText.length === 0 && !!isSearchOpen)}
								onClick={() => {
									if (searchValueText.length > 0) {
										onBlur?.();
										onSubmit({ label: searchValueText });
										return;
									}

									handleSearchClick();
									inputRef.current?.focus();
								}}
								edge="start"
							>
								<SearchIcon />
							</IconButton>
						</InputAdornment>
					),
					endAdornment:
						(isSearchOpen && searchValue.length > 0) ||
						(mobile && searchValue.length > 0 && searchValueText.length > 0) ? (
							<InputAdornment position="end">
								<Button
									sx={{
										padding: '0px',
										color: '#575555',
										fontSize: 'var(--font-size-small)',
										fontWeight: 'normal',
										height: '30px',
										'&:hover': {
											backgroundColor: 'transparent',
										},
									}}
									onClick={() => handleClearSearch()}
								>
									{'Borrar'}
								</Button>
							</InputAdornment>
						) : null,
				}}
				onChange={handleInputChange}
				autoComplete="false"
			/>
		);
	}
);
